(() => {
  // src/profiles/part-profiles.js
  var GEOMETRY_FAMILIES = Object.freeze({
    ROUNDED_STUD_PIVOT: "rounded-stud-pivot",
    STAGGERED_SWIVEL_HINGE: "staggered-swivel-hinge"
  });
  var BUILD_MODES = Object.freeze({
    FULL_CIRCLE: "full-circle",
    ARC: "arc"
  });
  function freezeCountRules(fullCircle) {
    return Object.freeze({
      fullCircle: Object.freeze(fullCircle),
      arc: Object.freeze({ minimum: 1, parity: "any" })
    });
  }
  function roundedProfile({ id, nominalLengthStuds, segmentLengthStuds }) {
    return Object.freeze({
      id,
      displayLabel: id,
      shortTypeLabel: `Rounded 1\xD7${nominalLengthStuds} pivot part`,
      imageFilename: `${id}.png`,
      imageAlt: `Part ${id}, rounded 1 by ${nominalLengthStuds} pivot part`,
      family: GEOMETRY_FAMILIES.ROUNDED_STUD_PIVOT,
      variant: null,
      nominalSize: Object.freeze({ widthStuds: 1, lengthStuds: nominalLengthStuds }),
      segmentLengthStuds,
      physicalPartsPerSegment: Object.freeze([
        Object.freeze({ partNumber: id, quantity: 1 })
      ]),
      countRules: freezeCountRules({ minimum: 3, parity: "any" })
    });
  }
  function hingeProfile({ id, displayLabel, variant, partNumbers }) {
    return Object.freeze({
      id,
      displayLabel,
      shortTypeLabel: `Staggered ${variant} hinge pair`,
      imageFilename: `${id}.png`,
      imageAlt: `Parts ${partNumbers[0]} and ${partNumbers[1]}, staggered ${variant} hinge pair`,
      family: GEOMETRY_FAMILIES.STAGGERED_SWIVEL_HINGE,
      variant,
      nominalSize: null,
      segmentLengthStuds: 2,
      physicalPartsPerSegment: Object.freeze(partNumbers.map((partNumber) => Object.freeze({ partNumber, quantity: 1 }))),
      countRules: freezeCountRules({ minimum: 3, parity: "any" })
    });
  }
  var PART_PROFILES = Object.freeze([
    roundedProfile({ id: "35480", nominalLengthStuds: 2, segmentLengthStuds: 1 }),
    roundedProfile({ id: "77850", nominalLengthStuds: 3, segmentLengthStuds: 2 }),
    roundedProfile({ id: "77845", nominalLengthStuds: 4, segmentLengthStuds: 3 }),
    hingeProfile({
      id: "2429-2430",
      displayLabel: "2429 + 2430",
      variant: "plate",
      partNumbers: ["2429", "2430"]
    }),
    hingeProfile({
      id: "3830-3831",
      displayLabel: "3830 + 3831",
      variant: "brick",
      partNumbers: ["3830", "3831"]
    })
  ]);
  var PROFILE_BY_ID = new Map(PART_PROFILES.map((profile) => [profile.id, profile]));
  function listPartProfiles() {
    return PART_PROFILES;
  }
  function getPartProfile(profileId) {
    return PROFILE_BY_ID.get(profileId) ?? null;
  }

  // src/presentation/formatting.js
  var NUMBER_FORMATTER = new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 3,
    useGrouping: false
  });
  function formatNumber(value) {
    if (!Number.isFinite(value)) {
      throw new TypeError("A finite number is required for display formatting.");
    }
    return NUMBER_FORMATTER.format(value);
  }
  function formatDegrees(value) {
    return `${formatNumber(value)}\xB0`;
  }
  function formatStuds(value) {
    const formattedValue = formatNumber(value);
    const unit = formattedValue === "1" ? "stud" : "studs";
    return `${formattedValue} ${unit}`;
  }
  function formatMillimeters(value) {
    return `${formatNumber(value)} mm`;
  }

  // src/presentation/result-view-model.js
  function createAssemblySummary(profile, assemblyRequirements) {
    if (profile.family === GEOMETRY_FAMILIES.STAGGERED_SWIVEL_HINGE) {
      const [firstPart, secondPart] = assemblyRequirements.physicalParts;
      return `${assemblyRequirements.totalAssemblies} hinge-pair assemblies \xB7 ${assemblyRequirements.layerCounts.join(
        " + "
      )} assemblies across the two layers \xB7 ${firstPart.quantity} \xD7 ${firstPart.partNumber} and ${secondPart.quantity} \xD7 ${secondPart.partNumber}`;
    }
    return `${assemblyRequirements.totalPhysicalParts} \xD7 part ${profile.id}`;
  }
  function createFullCircleResultViewModel({
    profile,
    geometry,
    assemblyRequirements
  }) {
    const hingeProfile2 = profile.family === GEOMETRY_FAMILIES.STAGGERED_SWIVEL_HINGE;
    return Object.freeze({
      selectedPart: profile.displayLabel,
      selectedPartType: profile.shortTypeLabel,
      countLabel: hingeProfile2 ? "Number of sides" : "Number of parts",
      countValue: geometry.segmentCount,
      rotationLabel: hingeProfile2 ? "Rotation per hinge" : "Rotation per part",
      rotationValue: formatDegrees(geometry.rotationDegrees),
      radiusLabel: "Centerline radius",
      radiusStuds: formatStuds(geometry.radiusStuds),
      radiusMillimeters: formatMillimeters(geometry.radiusMm),
      diameterLabel: "Centerline diameter",
      diameterStuds: formatStuds(geometry.diameterStuds),
      diameterMillimeters: formatMillimeters(geometry.diameterMm),
      assemblySummary: createAssemblySummary(profile, assemblyRequirements)
    });
  }
  function createArcResultViewModel({
    profile,
    geometry,
    assemblyRequirements
  }) {
    const hingeProfile2 = profile.family === GEOMETRY_FAMILIES.STAGGERED_SWIVEL_HINGE;
    return Object.freeze({
      selectedPart: profile.displayLabel,
      selectedPartType: profile.shortTypeLabel,
      countLabel: hingeProfile2 ? "Number of sides" : "Number of parts",
      countValue: geometry.segmentCount,
      sweepLabel: "Total arc angle",
      sweepValue: formatDegrees(geometry.sweepDegrees),
      rotationLabel: hingeProfile2 ? "Rotation per hinge" : "Rotation per part",
      rotationValue: formatDegrees(geometry.rotationDegrees),
      radiusLabel: "Centerline radius",
      radiusStuds: formatStuds(geometry.radiusStuds),
      radiusMillimeters: formatMillimeters(geometry.radiusMm),
      diameterLabel: "Implied full-circle diameter",
      diameterStuds: formatStuds(geometry.diameterStuds),
      diameterMillimeters: formatMillimeters(geometry.diameterMm),
      assemblySummary: createAssemblySummary(profile, assemblyRequirements)
    });
  }

  // src/core/validation.js
  var INTEGER_PATTERN = /^-?\d+$/;
  var DECIMAL_PATTERN = /^-?(?:\d+(?:\.\d+)?|\.\d+)$/;
  var NON_FINITE_PATTERN = /^(?:nan|[+-]?infinity)$/i;
  function valid(value, raw) {
    return Object.freeze({ ok: true, value, raw });
  }
  function invalid(raw, code) {
    return Object.freeze({ ok: false, raw, code });
  }
  function inputText(rawInput) {
    if (typeof rawInput !== "string" && typeof rawInput !== "number") return null;
    const raw = typeof rawInput === "string" ? rawInput : String(rawInput);
    return { raw, normalized: raw.trim() };
  }
  function parseSegmentCount(rawInput) {
    const input = inputText(rawInput);
    if (!input) return invalid(rawInput, "invalid-type");
    const { raw, normalized } = input;
    if (normalized === "") return invalid(raw, "required");
    if (NON_FINITE_PATTERN.test(normalized)) return invalid(raw, "non-finite");
    if (!INTEGER_PATTERN.test(normalized)) return invalid(raw, "invalid-integer");
    const value = Number(normalized);
    if (!Number.isSafeInteger(value)) return invalid(raw, "unsafe-integer");
    if (value <= 0) return invalid(raw, "positive-integer-required");
    return valid(value, raw);
  }
  function validateSegmentCount({ profile, mode, rawCount }) {
    if (!profile?.countRules) throw new TypeError("A valid part profile is required.");
    if (mode !== BUILD_MODES.FULL_CIRCLE && mode !== BUILD_MODES.ARC) {
      throw new TypeError(`Unsupported build mode: ${mode}`);
    }
    const parsed = parseSegmentCount(rawCount);
    if (!parsed.ok) return parsed;
    const rules = mode === BUILD_MODES.FULL_CIRCLE ? profile.countRules.fullCircle : profile.countRules.arc;
    if (parsed.value < rules.minimum) {
      return invalid(parsed.raw, "minimum-count");
    }
    if (rules.parity === "even" && parsed.value % 2 !== 0) {
      return invalid(parsed.raw, "even-count-required");
    }
    return parsed;
  }
  function validateArcAngle(rawInput) {
    const input = inputText(rawInput);
    if (!input) return invalid(rawInput, "invalid-type");
    const { raw, normalized } = input;
    if (normalized === "") return invalid(raw, "required");
    if (NON_FINITE_PATTERN.test(normalized)) return invalid(raw, "non-finite");
    if (!DECIMAL_PATTERN.test(normalized)) return invalid(raw, "invalid-number");
    const value = Number(normalized);
    if (!Number.isFinite(value)) return invalid(raw, "non-finite");
    if (value <= 0 || value >= 360) return invalid(raw, "sweep-out-of-range");
    return valid(value, raw);
  }

  // src/core/assemblies.js
  function freezePhysicalParts(parts) {
    return Object.freeze(parts.map((part) => Object.freeze(part)));
  }
  function getAssemblyRequirements({ profile, mode, segmentCount }) {
    const validation = validateSegmentCount({ profile, mode, rawCount: segmentCount });
    if (!validation.ok) {
      throw new RangeError(`Invalid segment count: ${validation.code}`);
    }
    const count = validation.value;
    if (profile.family === GEOMETRY_FAMILIES.ROUNDED_STUD_PIVOT) {
      return Object.freeze({
        kind: "single-part",
        geometricSegmentCount: count,
        totalPhysicalParts: count,
        physicalParts: freezePhysicalParts(profile.physicalPartsPerSegment.map((part) => ({
          partNumber: part.partNumber,
          quantity: part.quantity * count
        })))
      });
    }
    if (profile.family === GEOMETRY_FAMILIES.STAGGERED_SWIVEL_HINGE) {
      const firstLayerCount = Math.ceil(count / 2);
      const secondLayerCount = count - firstLayerCount;
      return Object.freeze({
        kind: "staggered-hinge",
        geometricSideCount: count,
        totalAssemblies: count,
        layerCounts: Object.freeze([firstLayerCount, secondLayerCount]),
        physicalParts: freezePhysicalParts(profile.physicalPartsPerSegment.map((part) => ({
          partNumber: part.partNumber,
          quantity: part.quantity * count
        })))
      });
    }
    throw new TypeError(`Unsupported geometry family: ${profile.family}`);
  }

  // src/core/geometry.js
  var MILLIMETERS_PER_STUD = 8;
  function assertPositiveFinite(value, name) {
    if (!Number.isFinite(value) || value <= 0) {
      throw new RangeError(`${name} must be a positive finite number.`);
    }
  }
  function assertSegmentCount(segmentCount, minimum) {
    if (!Number.isSafeInteger(segmentCount) || segmentCount < minimum) {
      throw new RangeError(`segmentCount must be a safe integer of at least ${minimum}.`);
    }
  }
  function studsToMillimeters(valueStuds) {
    if (!Number.isFinite(valueStuds)) {
      throw new RangeError("valueStuds must be finite.");
    }
    const valueMm = valueStuds * MILLIMETERS_PER_STUD;
    if (!Number.isFinite(valueMm)) {
      throw new RangeError("Millimeter conversion must remain finite.");
    }
    return valueMm;
  }
  function createGeometryResult({
    mode,
    segmentLengthStuds,
    segmentCount,
    sweepDegrees,
    rotationDegrees,
    rotationRadians,
    radiusStuds
  }) {
    const diameterStuds = radiusStuds * 2;
    return Object.freeze({
      mode,
      segmentLengthStuds,
      segmentCount,
      sweepDegrees,
      rotationDegrees,
      rotationRadians,
      radiusStuds,
      diameterStuds,
      radiusMm: studsToMillimeters(radiusStuds),
      diameterMm: studsToMillimeters(diameterStuds)
    });
  }
  function calculateFullCircle({ segmentLengthStuds, segmentCount }) {
    assertPositiveFinite(segmentLengthStuds, "segmentLengthStuds");
    assertSegmentCount(segmentCount, 3);
    const sweepDegrees = 360;
    const rotationDegrees = sweepDegrees / segmentCount;
    const rotationRadians = rotationDegrees * Math.PI / 180;
    const radiusStuds = segmentLengthStuds / (2 * Math.sin(Math.PI / segmentCount));
    return createGeometryResult({
      mode: "full-circle",
      segmentLengthStuds,
      segmentCount,
      sweepDegrees,
      rotationDegrees,
      rotationRadians,
      radiusStuds
    });
  }
  function calculateArc({ segmentLengthStuds, segmentCount, sweepDegrees }) {
    assertPositiveFinite(segmentLengthStuds, "segmentLengthStuds");
    assertSegmentCount(segmentCount, 1);
    if (!Number.isFinite(sweepDegrees) || sweepDegrees <= 0 || sweepDegrees >= 360) {
      throw new RangeError("sweepDegrees must be greater than 0 and less than 360.");
    }
    const rotationDegrees = sweepDegrees / segmentCount;
    const rotationRadians = rotationDegrees * Math.PI / 180;
    const radiusStuds = segmentLengthStuds / (2 * Math.sin(rotationRadians / 2));
    return createGeometryResult({
      mode: "arc",
      segmentLengthStuds,
      segmentCount,
      sweepDegrees,
      rotationDegrees,
      rotationRadians,
      radiusStuds
    });
  }

  // src/ui/state.js
  var DEFAULT_PROFILE_ID = "35480";
  var DEFAULT_SEGMENT_COUNT = "8";
  var DEFAULT_ARC_ANGLE = "90";
  function evaluateState({ profileId, mode, rawCount, rawArcAngle }) {
    const profile = getPartProfile(profileId);
    const countValidation = validateSegmentCount({
      profile,
      mode,
      rawCount
    });
    const arcAngleValidation = mode === BUILD_MODES.ARC ? validateArcAngle(rawArcAngle) : null;
    const validation = countValidation.ok && arcAngleValidation ? arcAngleValidation : countValidation;
    if (!countValidation.ok || arcAngleValidation && !arcAngleValidation.ok) {
      return Object.freeze({
        profileId,
        mode,
        rawCount,
        rawArcAngle,
        validation,
        countValidation,
        arcAngleValidation,
        geometry: null,
        assemblyRequirements: null
      });
    }
    const geometry = mode === BUILD_MODES.ARC ? calculateArc({
      segmentLengthStuds: profile.segmentLengthStuds,
      segmentCount: countValidation.value,
      sweepDegrees: arcAngleValidation.value
    }) : calculateFullCircle({
      segmentLengthStuds: profile.segmentLengthStuds,
      segmentCount: countValidation.value
    });
    return Object.freeze({
      profileId,
      mode,
      rawCount,
      rawArcAngle,
      validation,
      countValidation,
      arcAngleValidation,
      geometry,
      assemblyRequirements: getAssemblyRequirements({
        profile,
        mode,
        segmentCount: countValidation.value
      })
    });
  }
  function createInitialState() {
    return evaluateState({
      profileId: DEFAULT_PROFILE_ID,
      mode: BUILD_MODES.FULL_CIRCLE,
      rawCount: DEFAULT_SEGMENT_COUNT,
      rawArcAngle: DEFAULT_ARC_ANGLE
    });
  }
  function getCountPresetRenderState(state, presetCount) {
    const profile = getPartProfile(state.profileId);
    const availability = validateSegmentCount({
      profile,
      mode: state.mode,
      rawCount: presetCount
    });
    const supportedRawType = typeof state.rawCount === "string" || typeof state.rawCount === "number";
    const matchesCurrentCount = supportedRawType && String(state.rawCount).trim() === String(presetCount);
    return Object.freeze({
      disabled: !availability.ok,
      pressed: availability.ok && matchesCurrentCount
    });
  }
  function withSelectedProfile(state, profileId) {
    return evaluateState({
      profileId,
      mode: state.mode,
      rawCount: state.rawCount,
      rawArcAngle: state.rawArcAngle
    });
  }
  function withCountInput(state, rawCount) {
    return evaluateState({
      profileId: state.profileId,
      mode: state.mode,
      rawCount,
      rawArcAngle: state.rawArcAngle
    });
  }
  function withMode(state, mode) {
    return evaluateState({
      profileId: state.profileId,
      mode,
      rawCount: state.rawCount,
      rawArcAngle: state.rawArcAngle
    });
  }
  function withArcAngleInput(state, rawArcAngle) {
    return evaluateState({
      profileId: state.profileId,
      mode: state.mode,
      rawCount: state.rawCount,
      rawArcAngle
    });
  }
  function getArcAnglePresetRenderState(state, presetAngle) {
    const supportedRawType = typeof state.rawArcAngle === "string" || typeof state.rawArcAngle === "number";
    const matchesCurrentAngle = supportedRawType && String(state.rawArcAngle).trim() === String(presetAngle);
    return Object.freeze({ pressed: state.mode === BUILD_MODES.ARC && matchesCurrentAngle });
  }

  // src/ui/ui-text.js
  var UI_TEXT = Object.freeze({
    title: "Circle & Arc Calculator",
    introduction: "Plan smooth LEGO circles with supported rounded pivot parts and staggered hinge pairs.",
    fullCircleMode: "Full Circle",
    arcMode: "Arc",
    resultUnavailable: "Correct the count to see an updated Full Circle result.",
    arcResultUnavailable: "Correct the count and total arc angle to see an updated Arc result."
  });
  var VALIDATION_MESSAGES = Object.freeze({
    required: "Enter a count.",
    "invalid-type": "Enter the count as a number.",
    "invalid-integer": "Enter a whole number without extra characters.",
    "non-finite": "Enter a finite whole number.",
    "unsafe-integer": "Enter a smaller whole number.",
    "positive-integer-required": "Enter a count greater than zero.",
    "minimum-count": "This part needs a larger count for a Full Circle."
  });
  function getValidationMessage(validation) {
    if (validation.ok) {
      return "";
    }
    return VALIDATION_MESSAGES[validation.code] ?? "Enter a valid count.";
  }
  var ARC_ANGLE_VALIDATION_MESSAGES = Object.freeze({
    required: "Enter a total arc angle.",
    "invalid-type": "Enter the total arc angle as a number.",
    "invalid-number": "Enter a number without extra characters.",
    "non-finite": "Enter a finite number.",
    "sweep-out-of-range": "Enter an angle greater than 0\xB0 and less than 360\xB0."
  });
  function getArcAngleValidationMessage(validation) {
    if (!validation || validation.ok) {
      return "";
    }
    return ARC_ANGLE_VALIDATION_MESSAGES[validation.code] ?? "Enter a valid total arc angle.";
  }

  // src/ui/calculator.js
  var PART_PROFILES2 = listPartProfiles();
  var COUNT_PRESETS = Object.freeze([6, 7, 8, 9, 10, 12, 16]);
  var ARC_ANGLE_PRESETS = Object.freeze([45, 90, 180]);
  var nextInstanceId = 1;
  function escapeHtml(value) {
    return String(value).replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll('"', "&quot;").replaceAll("'", "&#039;");
  }
  function partCardsMarkup(instanceId, assetUrls) {
    return PART_PROFILES2.map((profile) => {
      const optionId = `${instanceId}-part-${profile.id}`;
      const imageUrl = assetUrls[profile.id] ?? "";
      return `
      <label class="htbi-circle-part-card" for="${optionId}">
        <input
          class="htbi-circle-part-card__input"
          id="${optionId}"
          type="radio"
          name="${instanceId}-part"
          value="${escapeHtml(profile.id)}"
          data-part-option
        >
        <span class="htbi-circle-part-card__body">
          <img
            class="htbi-circle-part-card__image"
            src="${escapeHtml(imageUrl)}"
            alt="${escapeHtml(profile.imageAlt)}"
            width="1254"
            height="1254"
          >
          <span class="htbi-circle-part-card__name">${escapeHtml(profile.displayLabel)}</span>
          <span class="htbi-circle-part-card__type">${escapeHtml(profile.shortTypeLabel)}</span>
          <span class="htbi-circle-part-card__selected" aria-hidden="true">Selected</span>
        </span>
      </label>
    `;
    }).join("");
  }
  function presetButtonsMarkup() {
    return COUNT_PRESETS.map(
      (count) => `
      <button
        class="htbi-circle-preset"
        type="button"
        data-count-preset="${count}"
        aria-pressed="false"
      >${count}</button>
    `
    ).join("");
  }
  function arcAnglePresetButtonsMarkup() {
    return ARC_ANGLE_PRESETS.map(
      (angle) => `
      <button
        class="htbi-circle-preset"
        type="button"
        data-arc-angle-preset="${angle}"
        aria-pressed="false"
      >${angle}\xB0</button>
    `
    ).join("");
  }
  function calculatorMarkup(instanceId, assetUrls) {
    return `
    <section class="htbi-circle-calculator" aria-labelledby="${instanceId}-title">
      <header class="htbi-circle-header">
        <p class="htbi-circle-header__eyebrow">Phase 1 browser tool</p>
        <h1 id="${instanceId}-title">${UI_TEXT.title}</h1>
        <p>${UI_TEXT.introduction}</p>
      </header>

      <section class="htbi-circle-step" aria-labelledby="${instanceId}-part-heading">
        <div class="htbi-circle-step__heading">
          <span aria-hidden="true">1</span>
          <h2 id="${instanceId}-part-heading">Choose a part</h2>
        </div>
        <fieldset class="htbi-circle-part-fieldset">
          <legend class="htbi-circle-sr-only">Supported LEGO part</legend>
          <div class="htbi-circle-part-grid">
            ${partCardsMarkup(instanceId, assetUrls)}
          </div>
        </fieldset>
      </section>

      <section class="htbi-circle-step" aria-labelledby="${instanceId}-count-heading">
        <div class="htbi-circle-step__heading">
          <span aria-hidden="true">2</span>
          <h2 id="${instanceId}-count-heading">Set the Full Circle count</h2>
        </div>

        <fieldset class="htbi-circle-mode-fieldset">
          <legend>Build mode</legend>
          <div class="htbi-circle-mode-options">
            <label class="htbi-circle-mode-option">
              <input type="radio" name="${instanceId}-mode" value="${BUILD_MODES.FULL_CIRCLE}" data-mode-option>
              <span>${UI_TEXT.fullCircleMode}</span>
            </label>
            <label class="htbi-circle-mode-option">
              <input type="radio" name="${instanceId}-mode" value="${BUILD_MODES.ARC}" data-mode-option>
              <span>${UI_TEXT.arcMode}</span>
            </label>
          </div>
        </fieldset>

        <fieldset class="htbi-circle-count-fieldset">
          <legend>Quick counts</legend>
          <div class="htbi-circle-presets" data-count-presets>
            ${presetButtonsMarkup()}
          </div>
          <div class="htbi-circle-custom-count">
            <label for="${instanceId}-count" data-count-label>Custom number of parts</label>
            <input
              id="${instanceId}-count"
              type="text"
              inputmode="numeric"
              autocomplete="off"
              data-count-input
              aria-describedby="${instanceId}-count-hint ${instanceId}-count-error"
            >
            <p id="${instanceId}-count-hint" class="htbi-circle-hint">
              Choose a preset or enter a whole number.
            </p>
            <p id="${instanceId}-count-error" class="htbi-circle-error" data-count-error></p>
          </div>
        </fieldset>

        <fieldset class="htbi-circle-arc-controls" data-arc-controls hidden>
          <legend>Total arc angle</legend>
          <div class="htbi-circle-presets" data-arc-angle-presets>
            ${arcAnglePresetButtonsMarkup()}
          </div>
          <div class="htbi-circle-custom-angle">
            <label for="${instanceId}-arc-angle">Custom total arc angle</label>
            <input
              id="${instanceId}-arc-angle"
              type="text"
              inputmode="decimal"
              autocomplete="off"
              data-arc-angle-input
              aria-describedby="${instanceId}-arc-angle-hint ${instanceId}-arc-angle-error"
            >
            <p id="${instanceId}-arc-angle-hint" class="htbi-circle-hint">
              Choose a preset or enter an angle greater than 0\xB0 and less than 360\xB0.
            </p>
            <p id="${instanceId}-arc-angle-error" class="htbi-circle-error" data-arc-angle-error></p>
          </div>
        </fieldset>
      </section>

      <section class="htbi-circle-step htbi-circle-step--result" aria-labelledby="${instanceId}-result-heading">
        <div class="htbi-circle-step__heading">
          <span aria-hidden="true">3</span>
          <h2 id="${instanceId}-result-heading">Full Circle result</h2>
        </div>
        <div class="htbi-circle-result" data-result aria-live="polite"></div>
      </section>

      <p class="htbi-circle-disclaimer">
        Geometry is calculated at the part centerline. Real builds still depend on part clearance, clutch, and assembly conditions.
      </p>
    </section>
  `;
  }
  function resultMarkup(viewModel) {
    const sweepMarkup = viewModel.sweepLabel ? `
      <div>
        <dt>${escapeHtml(viewModel.sweepLabel)}</dt>
        <dd>${escapeHtml(viewModel.sweepValue)}</dd>
      </div>
    ` : "";
    return `
    <div class="htbi-circle-result__part">
      <span>Selected part</span>
      <strong>${escapeHtml(viewModel.selectedPart)}</strong>
      <small>${escapeHtml(viewModel.selectedPartType)}</small>
    </div>
    <dl class="htbi-circle-result__grid">
      <div>
        <dt>${escapeHtml(viewModel.countLabel)}</dt>
        <dd>${viewModel.countValue}</dd>
      </div>
      ${sweepMarkup}
      <div>
        <dt>${escapeHtml(viewModel.rotationLabel)}</dt>
        <dd>${escapeHtml(viewModel.rotationValue)}</dd>
      </div>
      <div>
        <dt>${escapeHtml(viewModel.radiusLabel)}</dt>
        <dd>${escapeHtml(viewModel.radiusStuds)} <small>${escapeHtml(viewModel.radiusMillimeters)}</small></dd>
      </div>
      <div>
        <dt>${escapeHtml(viewModel.diameterLabel)}</dt>
        <dd>${escapeHtml(viewModel.diameterStuds)} <small>${escapeHtml(viewModel.diameterMillimeters)}</small></dd>
      </div>
    </dl>
    <p class="htbi-circle-result__assembly">${escapeHtml(viewModel.assemblySummary)}</p>
  `;
  }
  function readAssetUrls(root) {
    return Object.fromEntries(
      PART_PROFILES2.map((profile) => [
        profile.id,
        root.getAttribute(`data-image-${profile.id}`) ?? ""
      ])
    );
  }
  function createCircleCalculator(root) {
    const instanceId = `htbi-circle-${nextInstanceId}`;
    nextInstanceId += 1;
    const assetUrls = readAssetUrls(root);
    root.innerHTML = calculatorMarkup(instanceId, assetUrls);
    const countInput = root.querySelector("[data-count-input]");
    const countLabel = root.querySelector("[data-count-label]");
    const countError = root.querySelector("[data-count-error]");
    const result = root.querySelector("[data-result]");
    const countHeading = root.querySelector(`#${instanceId}-count-heading`);
    const resultHeading = root.querySelector(`#${instanceId}-result-heading`);
    const arcControls = root.querySelector("[data-arc-controls]");
    const arcAngleInput = root.querySelector("[data-arc-angle-input]");
    const arcAngleError = root.querySelector("[data-arc-angle-error]");
    let state = createInitialState();
    function render() {
      const selectedProfile = PART_PROFILES2.find(
        (profile) => profile.id === state.profileId
      );
      const hingeProfile2 = selectedProfile.family === GEOMETRY_FAMILIES.STAGGERED_SWIVEL_HINGE;
      const arcMode = state.mode === BUILD_MODES.ARC;
      for (const option of root.querySelectorAll("[data-part-option]")) {
        option.checked = option.value === state.profileId;
      }
      for (const option of root.querySelectorAll("[data-mode-option]")) {
        option.checked = option.value === state.mode;
      }
      for (const preset of root.querySelectorAll("[data-count-preset]")) {
        const presetCount = Number(preset.dataset.countPreset);
        const presetState = getCountPresetRenderState(state, presetCount);
        preset.setAttribute("aria-pressed", String(presetState.pressed));
        preset.disabled = presetState.disabled;
      }
      for (const preset of root.querySelectorAll("[data-arc-angle-preset]")) {
        const presetAngle = Number(preset.dataset.arcAnglePreset);
        const presetState = getArcAnglePresetRenderState(state, presetAngle);
        preset.setAttribute("aria-pressed", String(presetState.pressed));
      }
      countInput.value = state.rawCount;
      countInput.setAttribute("aria-invalid", String(!state.countValidation.ok));
      countLabel.textContent = hingeProfile2 ? "Custom number of sides" : "Custom number of parts";
      countError.textContent = getValidationMessage(state.countValidation);
      countHeading.textContent = arcMode ? "Set the Arc count" : "Set the Full Circle count";
      resultHeading.textContent = arcMode ? "Arc result" : "Full Circle result";
      arcControls.hidden = !arcMode;
      arcAngleInput.value = state.rawArcAngle;
      arcAngleInput.setAttribute(
        "aria-invalid",
        String(arcMode && !state.arcAngleValidation.ok)
      );
      arcAngleError.textContent = arcMode ? getArcAngleValidationMessage(state.arcAngleValidation) : "";
      if (!state.validation.ok) {
        const unavailableText = arcMode ? UI_TEXT.arcResultUnavailable : UI_TEXT.resultUnavailable;
        result.innerHTML = `<p class="htbi-circle-result__empty">${unavailableText}</p>`;
        return;
      }
      const createResultViewModel = arcMode ? createArcResultViewModel : createFullCircleResultViewModel;
      const viewModel = createResultViewModel({
        profile: selectedProfile,
        geometry: state.geometry,
        assemblyRequirements: state.assemblyRequirements
      });
      result.innerHTML = resultMarkup(viewModel);
    }
    root.addEventListener("change", (event) => {
      const modeOption = event.target.closest("[data-mode-option]");
      if (modeOption) {
        state = withMode(state, modeOption.value);
        render();
        return;
      }
      const option = event.target.closest("[data-part-option]");
      if (!option) {
        return;
      }
      state = withSelectedProfile(state, option.value);
      render();
    });
    root.addEventListener("keydown", (event) => {
      const option = event.target.closest("[data-part-option]");
      const navigationKeys = ["ArrowLeft", "ArrowUp", "ArrowRight", "ArrowDown", "Home", "End"];
      if (!option || !navigationKeys.includes(event.key)) {
        return;
      }
      const options = [...root.querySelectorAll("[data-part-option]")];
      const currentIndex = options.indexOf(option);
      const lastIndex = options.length - 1;
      let nextIndex = currentIndex;
      if (event.key === "Home") nextIndex = 0;
      if (event.key === "End") nextIndex = lastIndex;
      if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
        nextIndex = currentIndex === 0 ? lastIndex : currentIndex - 1;
      }
      if (event.key === "ArrowRight" || event.key === "ArrowDown") {
        nextIndex = currentIndex === lastIndex ? 0 : currentIndex + 1;
      }
      event.preventDefault();
      options[nextIndex].focus();
      options[nextIndex].click();
    });
    root.addEventListener("click", (event) => {
      const arcAnglePreset = event.target.closest("[data-arc-angle-preset]");
      if (arcAnglePreset) {
        state = withArcAngleInput(state, arcAnglePreset.dataset.arcAnglePreset);
        render();
        return;
      }
      const preset = event.target.closest("[data-count-preset]");
      if (!preset || preset.disabled) {
        return;
      }
      state = withCountInput(state, preset.dataset.countPreset);
      render();
    });
    countInput.addEventListener("input", () => {
      state = withCountInput(state, countInput.value);
      render();
    });
    arcAngleInput.addEventListener("input", () => {
      state = withArcAngleInput(state, arcAngleInput.value);
      render();
    });
    render();
    root.dataset.htbiCircleMounted = "true";
    return Object.freeze({
      getState: () => state
    });
  }

  // src/ui/main.js
  function mountCircleCalculators(scope = document) {
    const roots = scope.querySelectorAll(
      "[data-htbi-circle-calculator]:not([data-htbi-circle-mounted])"
    );
    return [...roots].map((root) => createCircleCalculator(root));
  }
  function initialize() {
    mountCircleCalculators();
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initialize, { once: true });
  } else {
    initialize();
  }
})();
